import { CalendarDays } from 'lucide-react';

import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { PortalEmptyState } from '@/modules/hiring-manager/components/portalEmptyState/PortalEmptyState';
import { PortalListSkeleton } from '@/modules/hiring-manager/components/PortalList';
import { isJobVisibleToHiringManagers } from '@/modules/jobs/jobStates';
import { useJobsQuery } from '@/modules/jobs/queries/useJobsQuery';

interface InterviewsEmptyProps {
  companyId: string;
  /** Tenant selection carried on every portal link. */
  searchSuffix: string;
  userId?: string;
}

/**
 * Why the interview list is empty. Mounted only when it is, so the jobs
 * lookup it needs never costs a request on a page that has interviews; the
 * params match the dashboard's so a user arriving from there reads its cache.
 */
export function InterviewsEmpty({
  companyId,
  searchSuffix,
  userId,
}: InterviewsEmptyProps) {
  const { plan, isLoading, error } = useWorkspacePlan();
  const canEditJob = !isLoading && !error && plan !== 'managed';
  const jobsQuery = useJobsQuery({ companyId });
  const hasNoLiveJobs =
    jobsQuery.isSuccess &&
    !(jobsQuery.data ?? []).some((job) =>
      isJobVisibleToHiringManagers(job.state),
    );
  const draftJob = jobsQuery.data?.find(
    (job) =>
      job.sourcingState === 'Draft' &&
      userId !== undefined &&
      job.ownerId === userId,
  );

  if (jobsQuery.isPending) {
    return <PortalListSkeleton lines={2} rows={1} />;
  }

  if (jobsQuery.isError) {
    return (
      <HMLoadError
        description="We could not check whether this workspace has any jobs. Check your connection and try again."
        isRetrying={jobsQuery.isFetching}
        onRetry={() => void jobsQuery.refetch()}
        title="Jobs could not be loaded"
      />
    );
  }

  if (hasNoLiveJobs) {
    return (
      <PortalEmptyState
        action={
          draftJob && canEditJob
            ? {
                label: 'Finish draft',
                to: `/hm/jobs/${draftJob.id}/edit${searchSuffix}`,
              }
            : { label: 'Create a job', to: `/hm/chat${searchSuffix}` }
        }
        description={
          plan === 'managed'
            ? 'Create a job for your Finlay recruiter. Scheduled interviews will appear here.'
            : draftJob
              ? 'Finish your draft, then start sourcing candidates.'
              : 'Create a job, then start sourcing candidates.'
        }
        icon={CalendarDays}
        title="No interviews yet"
      />
    );
  }

  return (
    <PortalEmptyState
      description={
        plan === 'managed'
          ? 'Finlay coordinates timing after you approve a candidate. Confirmed interviews appear here.'
          : 'Interview scheduling is coming soon. Scheduled interviews will appear here when it is available.'
      }
      icon={CalendarDays}
      live={plan === 'managed'}
      title="No interviews yet"
    />
  );
}
